import {
  DirectiveResult,
  html,
  ovl,
  SYSConfig,
  SYSDialog,
  SYSFeature,
  SYSFeatures,
  SYSPermissions,
  SYSScreen,
  SYSState,
  SYSTableDefIds,
  TemplateResult,
  unsafeHTML,
} from "../"
import { sortedReplacer } from "../library/forms/helper"

import type { FieldFormat } from "../library/forms/sysformelement"
import { SnackAdd } from "../library/snackapi"
import { _TableRowError } from "../library/table/actions"
import { TableSaveRow } from "../library/table/tableapi"
import { sessionStore, stateStore } from "../offlinestorage"
import type { PermissionSettings, SYSPermissionModifiers } from "../state"
import { _RenderTracking } from "../tracker/tracker"
import { Rehydrate } from "./actions"
import { displayFormats } from "./displayformats"
import { PermissionHas, SessionUpdate } from "./globalapi"
import { HookAppFileOpen, HookAppSaveState } from "./hooks"

// we need those because Object keys are always strings and we want sometimes to have null as key
// mind the space. it makes sure they are at the the top of the list if sorted
//export const SYSNullKey = "@@SYSNull"

// we need those because Object keys are always strings and we want sometimes to have null as key
// mind the space. it makes sure they are at the the top of the list if sorted
//export const SYSNullKey = "@@SYSNull"
export { _customElementList, displayFormats as DisplayFormats }

export let _globals = { lastValidFocusId: "", previousValidFocusId: "" }

//export let modalDialog: GlobalModalDialogState = { text: undefined }
export let translationData = {}

export type SYSCustomElementListEntry = {
  tagName: string
  comp: any
  screen?: SYSScreen
  dialog?: SYSDialog
}
let _customElementList: SYSCustomElementListEntry[] = []
export let _registerComponents = { _customElementList }
export const OvlAddComponent = (tagName: string, comp: any) => {
  _registerComponents._customElementList.push({ tagName, comp })
}
export const OvlAddScreen = (comp: any, screen: SYSScreen) => {
  let tagName = `scn-${screen.toLowerCase()}`
  _registerComponents._customElementList.push({ tagName, comp, screen })
}

export const OvlAddDialog = (comp: any, dialog: SYSDialog) => {
  _registerComponents._customElementList.push({
    tagName: `dlg-${dialog.toLowerCase()}`,
    comp,
    screen: undefined,
    dialog,
  })
}

export const OvlAddModuleVersion = (
  name: string,
  version: string | { version: string; commit?: string }
) => {
  if (!SYSConfig._system.versionInfo.frontend.modules) {
    SYSConfig._system.versionInfo.frontend.modules = {}
  }
  SYSConfig._system.versionInfo.frontend.modules[name] = version
}

// we keep in those a shortcut to ovl.getState().ovl.language...
// to simplify tracking
export let languageRef

type GlobalModalDialogState = {
  text: string | TemplateResult
}

export const _HasPermission = (
  permission: SYSPermissions,
  modifier?: SYSPermissionModifiers
) => {
  let user = ovl.getState().ovl.user
  if (!permission /*|| user.role === "Admin"*/) {
    return true
  }

  let permissions = user.permissions
  if (!permissions) {
    // for compatibility reasons
    return true
  }

  // admin should always be able to access users/userroles/roles
  if (user.role === "Admin") {
    if (
      (permission === "SYSUA" || permission === "SYSCA") &&
      (!modifier || modifier === "R")
    ) {
      return true
    } else if (permission === "SYSUR" || permission === "SYSRA") {
      return true
    }
  }

  if (!permissions[permission]) {
    return false
  }
  if (!modifier) {
    return true
  }
  return !!permissions[permission].modifiers[modifier]
}

export const _GetPermissionSettings = (
  permission: SYSPermissions
): PermissionSettings => {
  let user = ovl.getState().ovl.user
  let permissions = user.permissions

  if (!permissions) {
    // for compatibility reasons
    return undefined
  }
  if (!permissions[permission]) {
    return undefined
  }

  return permissions[permission]
}

export const getLocalTimestampString = (nd: Date): string => {
  var tzoffset = nd.getTimezoneOffset() * 60000 //offset in milliseconds
  return new Date(nd.getTime() - tzoffset).toISOString().slice(0, -1)
}

export const getDateISOString = (date: Date): string => {
  return (
    date.getFullYear().toString() +
    "-" +
    (date.getMonth() + 1).toString().padStart(2, "0") +
    "-" +
    date.getDate().toString().padStart(2, "0") +
    "T00:00:00"
  )
}
export const getOnlyDateISOString = (date: Date): string => {
  return (
    date.getFullYear().toString() +
    "-" +
    (date.getMonth() + 1).toString().padStart(2, "0") +
    "-" +
    date.getDate().toString().padStart(2, "0")
  )
}

export const getOnlyDate = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

//@ts-ignore
let SYSTimestamp = 0
export let SYSSaveState = { SYSTimestamp }
let uuidCounter = 0
export const SYSSeqId = () => {
  let appId = SYSConfig._system.appId
  let ticks = new Date().getTime().toString().padStart(19, "0")
  let userId = ovl.getState().ovl.user.userCode
  let userIdS = (userId !== undefined ? userId.toString() : "").padStart(6, "0")
  if (uuidCounter > 9999) {
    uuidCounter = 0
  }
  let counter = uuidCounter.toString().padStart(4, "0")
  uuidCounter++
  return `${appId}${ticks}${userIdS}${counter}`
}

export function isTouch() {
  return "ontouchstart" in window
}

let _isMobile = undefined
export function isMobile() {
  if (_isMobile === undefined) {
    var check = false
    ;(function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true
      //@ts-ignore
    })(navigator.userAgent || navigator.vendor || window.opera)
    _isMobile = check
  }
  return _isMobile
}

let _isIOS = undefined
declare var window: any
export function isIOS() {
  if (_isIOS === undefined) {
    _isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  }
  return _isIOS
}

let _isSafari = undefined

export const isSafari = () => {
  if (_isSafari === undefined) {
    _isSafari =
      navigator.userAgent.indexOf("Safari") != -1 &&
      navigator.userAgent.indexOf("Chrome") == -1
  }
  return _isSafari
}

let _isFirefox = undefined
export const isFirefox = () => {
  if (_isFirefox === undefined) {
    _isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1
  }
  return _isFirefox
}

let _hasOSReducedMotion
export function hasOSReducedMotion() {
  if (_hasOSReducedMotion === undefined) {
    const query = "(prefers-reduced-motion: reduce)"
    _hasOSReducedMotion = window.matchMedia(query).matches
  }
  return _hasOSReducedMotion
}

export const getDateValue = (value: any, format?: FieldFormat): string => {
  if (!value) {
    return ""
  }
  let fmt
  if (format) {
    switch (format) {
      case "timestampWithoutSecond":
      case "timestampWithoutSecond_break":
        fmt = displayFormats.date.timestampWithoutSecond
        break
      case "timestamp":
      case "timestamp_break":
        fmt = displayFormats.date.defaultTimestamp
        break
      case "2digitsYear":
        fmt = displayFormats.date._2DigitsYear
        break
      case "4digitsYear":
        fmt = displayFormats.date._4DigitsYear
        break
    }
  }
  if (!fmt) {
    if (isMobile()) {
      fmt = displayFormats.date._2DigitsYear
    } else {
      fmt = displayFormats.date.default
    }
  }
  let res
  if (typeof value.getMonth !== "function") {
    let dval = new Date(value)
    // value = value.split("/").join("-")
    // let dvals = value.split("-")
    // let dval = new Date(
    //   parseInt(dvals[0]),
    //   parseInt(dvals[1]) - 1,
    //   parseInt(dvals[2])
    // )

    res = fmt.format(dval)
  } else {
    res = fmt.format(value)
  }
  if (format && format.endsWith("_break")) {
    res = res.replace(", ", "\n")
  } else if (
    SYSConfig.global?.removeCommaFromTimestamp &&
    (format === "timestampWithoutSecond" || format === "timestamp")
  ) {
    let sdf = res.split(", ")
    if (sdf.length === 2) {
      res = `${sdf[0]} ${sdf[1]}`
    }
  }
  return res
}
export const getDecimalValue = (value: number, format?: FieldFormat) => {
  if (value === null || value === undefined) {
    return ""
  }
  let fmt = displayFormats.decimal.default

  if (format) {
    switch (format) {
      case "4digits":
        fmt = displayFormats.decimal._4Digits
        break
      case "3digits":
        fmt = displayFormats.decimal._3Digits
        break
      case "0digits":
        fmt = displayFormats.decimal._0Digits
        break
    }
  }
  return fmt.format(value)
}

export const getIntegerValue = (value: number, format?: FieldFormat) => {
  if (value === null || value === undefined) {
    return ""
  }
  let fmt = displayFormats.int.default
  if (format) {
    switch (format) {
      case "2digitsInteger":
        fmt = displayFormats.int._2DigitsInteger
        break
      case "3digitsInteger":
        fmt = displayFormats.int._3DigitsInteger
        break
      case "4digitsInteger":
        fmt = displayFormats.int._4DigitsInteger
        break
      case "5digitsInteger":
        fmt = displayFormats.int._5DigitsInteger
        break
      case "6digitsInteger":
        fmt = displayFormats.int._6DigitsInteger
        break
      case "8digitsInteger":
        fmt = displayFormats.int._8DigitsInteger
        break
      case "16digitsInteger":
        fmt = displayFormats.int._16DigitsInteger
        break
    }
  }
  return fmt.format(value)
}

// convienience function for date values
export let D = (date: string | Date) => {
  return getDateValue(date)
}
export let D2 = (date: string | Date) => {
  return getDateValue(date, "2digitsYear")
}

export let TS1 = (date: string | Date) => {
  return getDateValue(date, "timestamp")
}

export let TS2 = (date: string | Date) => {
  return getDateValue(date, "timestampWithoutSecond")
}

// convienience function for decimal values
export let N = (decimal: number) => {
  return getDecimalValue(decimal)
}

export let N0 = (decimal: number) => {
  return getDecimalValue(decimal, "0digits")
}

// convienience function for decimal values
export let N4 = (decimal: number) => {
  return getDecimalValue(decimal, "4digits")
}

export let I2 = (int: number) => {
  return getIntegerValue(int, "2digitsInteger")
}
export let I3 = (int: number) => {
  return getIntegerValue(int, "3digitsInteger")
}
export let I4 = (int: number) => {
  return getIntegerValue(int, "4digitsInteger")
}

// export const addGlobalPersistEventListeners = () => {
//   document.addEventListener("visibilitychange", visibilityChange)
//   document.addEventListener("focusout", (e) => focusOut(e))
// }

// export const focusOut = async (event) => {
//   if (SYSConfig.offline.enabled) {
//     if (!event.relatedTarget) {
//       await saveState(false, "FocusOut")
//     }
//   }
// }

export const stringifyReplacer = (key, value) =>
  typeof value === "undefined" ? null : value

// export const visibilityChange = async (event) => {
//   if (SYSConfig.offline.enabled) {
//     // console.log(document.visibilityState)
//     // fires when user switches tabs, apps, goes to homescreen, etc.
//     //@ts-ignore
//     if (
//       document.visibilityState === "hidden" ||
//       //@ts-ignore
//       document.visibilityState === "unloaded"
//     ) {
//       if (isIOS()) {
//         saveState(false, "Visibility")
//       } else {
//         await saveState(false, "Visibility")
//       }
//     }
//   }
//   if (isIOS() && document.visibilityState === "visible") {
//     // rehydrate state here on ios. eg. switching from standalone app to a downloaded doc and back needs to rehydrate on ios (not on android,chrome)
//     SnackAdd("REHYYYYDRAAATE")
//     await ovl.getActions().ovl.internal._RehydrateApp()
//   }
// }

export type UIContextType = {
  feature: string
  tableDefId: SYSTableDefIds
  formType: string
  formId: any
  screen: string
  screenInstance: string
}

export const rehydrateUserStateFromFile = async () => {
  const [fileHandle] = await window.showOpenFilePicker()

  const file = await fileHandle.getFile()
  const contents = await file.text()
  await Rehydrate(ovl.getActions(), undefined, undefined, contents)
}

export const rehydrateUserState = async (
  id?: string,
  fn?: (state: SYSState) => {}
) => {
  if (id === undefined) {
    id = ""
  }
  await Rehydrate(ovl.getActions(), id, fn)
}

export const saveUserState = (id?: string) => {
  if (id === undefined) {
    id = ""
  }
  saveState("User", id)
}

export const saveState = async (reason: string, testId?: string) => {
  if (
    testId !== undefined ||
    (SYSConfig.offline.enabled && !logoutAndClearFlag)
  ) {
    //@ts-ignore

    let stateStoreId = SYSConfig._system.statePersistVersion.toString()
    if (testId !== undefined) {
      stateStoreId = `${stateStoreId}_User_${testId}`
    }
    console.log("save state to: " + stateStoreId)
    // let refstate = ovl.getState()
    let newObj: SYSState = JSON.parse(
      JSON.stringify(ovl.getState()),
      stringifyReplacer
    )
    newObj.ovl.uiState.stateSavedReason = reason
    newObj.ovl.app.ready = false
    let dialogsState = newObj.ovl.dialogs
    Object.keys(dialogsState).forEach((f) => {
      Object.keys(dialogsState[f].instance).forEach((instance) => {
        if (dialogsState[f].instance[instance].visible) {
          if (
            dialogsState[f].instance?.[instance]?.modal === true ||
            dialogsState[f].rehydratable === false
          ) {
            dialogsState[f].instance[instance].visible = false
          }
        }
      })
    })
    // // clear out all processs isntances
    // let processState = newObj.ovl.longRunningProcessTracker
    // Object.keys(newObj.ovl.longRunningProcessTracker).forEach((k) => {
    //   newObj.ovl.longRunningProcessTracker[k].instance = {}
    // })

    if (ovl.getActions().app.global.hooks?.[HookAppSaveState]) {
      ovl.getActions().app.global.hooks?.[HookAppSaveState](newObj)
    }
    return stateStore.set(stateStoreId, newObj)
  }
}
export let logoutAndClearFlag = false
export let gotoFileFlag = false

export function IsComplexType(value) {
  return (
    (typeof value === "object" && value !== null) || typeof value === "function"
  )
}

export const logout = async () => {
  //ovl.getActions().ovl.indicator.SetIndicatorOpen()
  if (SYSConfig.offline.enabled) {
    logoutAndClearFlag = true
    try {
      // 1. unregister sw
      let regs = await navigator.serviceWorker.getRegistrations()
      if (regs) {
        await Promise.all(regs.map(async (reg) => await reg.unregister()))
      }
      // // 2. get rid of any indexeddb state
      // await stateStore.clear()
      // delete sessionstore
      await sessionStore.clear()
      // 3. get rid of any cached static assets
      // this is no more necessary since serviceworker iutsel deletes the cache
      let cacheKeys = await caches.keys()
      await Promise.all(
        cacheKeys.map(async (cacheName) => await caches.delete(cacheName))
      )
    } catch (e) {}
  }
  //await SessionUpdate("Token", undefined)
  location.reload()
  //window.location.search = window.location.search.replace("ovlreset", "")
}

export const toggleDebugTracking = () => {
  if (SYSConfig._system.debugTracking) {
    SYSConfig._system.debugTracking = false
    console.log("Disabled Debug Tracking")
  } else {
    SYSConfig._system.debugTracking = true
    console.log("Enabled Debug Tracking")
  }
}

export const logState = (logProxy: boolean) => {
  console.log("ovl state:")
  if (logProxy) {
    console.log(ovl.getState())
  } else {
    console.log(JSON.parse(JSON.stringify(ovl.getState()), stringifyReplacer))
  }
}

export const logRenderTracking = () => {
  console.log("ovl renderTracking:")
  console.log(_RenderTracking)
}

export const logFormValidationErrors = () => {
  console.log("todo...ovl forms having validation errors:")
  // let forms = ovl.getState().ovl.forms
  // let res = []
  // Object.keys(forms).forEach((f) => {
  //   let formInstance = forms[f]
  //   Object.keys(formInstance).forEach((fi) => {
  //     let formState: SYSFormState = forms[f][fi]

  //     let fieldValidation = Object.keys(formState.fields)
  //       .filter((f) => formState.fields[f].validationResult.length > 0)
  //       .map((m) => {
  //         let res: any = {}
  //         res[formState.fields[m].fieldKey] = formState.fields[m]
  //         res["errors"] = formState.fields[m].validationResult
  //         return res
  //       })
  //     if (formState.validationResult.visibleErrors.length > 0) {
  //       res.push({
  //         formtype: formState.formType,
  //         formId: formState.formId,
  //         formErrors: formState.validationResult.visibleErrors,
  //         fieldErrors: fieldValidation,
  //       })
  //     }
  //   })
  // })
  // console.log(JSON.parse(JSON.stringify(res, stringifyReplacer)))
}

export const logActions = () => {
  console.log("ovl actions:")
  console.log(ovl.getActions())
}

export const logVersion = () => {
  console.log("ovl versioninfo:")
  const versionInfo = SYSConfig._system.versionInfo
  const rows: Record<string, any> = {}

  const ensureRow = (name: string) => {
    if (!rows[name]) {
      rows[name] = {
        Name: name,
        "FE Version": "",
        "FE Commit": "",
        "BE Version": "",
        "BE Commit": "",
      }
    }
    return rows[name]
  }

  // Frontend
  if (versionInfo.frontend) {
    ;["app", "ovl"].forEach((key) => {
      const data = versionInfo.frontend[key]
      if (data) {
        const row = ensureRow(key.toUpperCase())
        row["FE Version"] = data.version
        row["FE Commit"] = data.commit || ""
      }
    })
    if (versionInfo.frontend.modules) {
      Object.entries(versionInfo.frontend.modules).forEach(([name, mod]) => {
        const row = ensureRow(name)
        if (typeof mod === "string") {
          row["FE Version"] = mod
        } else {
          row["FE Version"] = mod.version
          row["FE Commit"] = mod.commit || ""
        }
      })
    }
  }

  // Backend
  if (versionInfo.backend) {
    ;["app", "ovl"].forEach((key) => {
      const data = versionInfo.backend[key]
      if (data) {
        const row = ensureRow(key.toUpperCase())
        row["BE Version"] = data.version
        row["BE Commit"] = data.commit || ""
      }
    })
    if (versionInfo.backend.modules) {
      Object.entries(versionInfo.backend.modules).forEach(([name, mod]) => {
        const row = ensureRow(name)
        if (typeof mod === "string") {
          row["BE Version"] = mod
        } else {
          row["BE Version"] = mod.version
          row["BE Commit"] = mod.commit || ""
        }
      })
    }
  }

  console.table(rows, ["FE Version", "FE Commit", "BE Version", "BE Commit"])
}

export let focusIdNotFound = undefined
export const _SetFocus = (
  id: string,
  forceSelect?: boolean,
  preventScroll?: boolean,
  delayMS?: number
): boolean => {
  if (delayMS) {
    setTimeout(() => {
      _setFocusHelper(id, forceSelect, preventScroll, delayMS)
    }, delayMS)
    return
  }
  return _setFocusHelper(id, forceSelect, preventScroll, delayMS)
}

const _setFocusHelper = (
  id: string,
  forceSelect?: boolean,
  preventScroll?: boolean,
  delayMS?: number
) => {
  let el: any = document.getElementById(id)
  if (el === undefined) {
    focusIdNotFound = id
    return false
  }
  let elid = el.id
  if (document.activeElement.id === elid) {
    return true
  }
  focusIdNotFound = undefined
  el.focus({
    preventScroll: preventScroll === true,
  })

  if (el.setSelectionRange) {
    let val = el.value
    if (val) {
      try {
        el.setSelectionRange(val.length, val.length)
      } catch {}
    }
  }
  if (
    forceSelect === true ||
    (forceSelect === undefined && el.classList.contains("ovl-focusselected"))
  ) {
    try {
      el.select()
    } catch {}
  }

  if (delayMS === 0 && document.activeElement.id !== elid) {
    _SetFocus(id, forceSelect, preventScroll, 100)
  }

  el = null
  return true
}

export const _ShowFile = async (
  blob: any,
  mimeType: string = undefined,
  feature: SYSFeature = undefined,
  fileName: string = undefined,
  responseHeaders: any = undefined
) => {
  if (responseHeaders) {
    if (!fileName) {
      // if no fileName provided get it from the contentdsipositionheader

      try {
        fileName = responseHeaders
          .get("content-disposition")
          .split(";")[1]
          .split("=")[1]
          .replace('"', "")
          .replace('"', "")
      } catch {}
      if (!mimeType) {
        mimeType = responseHeaders.get["content-type"]
      }
    }
  }
  let fileOpenHook = ovl.getActions().app.global?.hooks?.[HookAppFileOpen]
  if (fileOpenHook) {
    await fileOpenHook({
      blob,
      mimeType,
      feature,
      fileName,
      responseHeaders,
    })
  } else {
    console.error("ovl config:  HookAppFileOpen not defined")
    // //@ts-ignore
    // window.URL = window.URL || window.webkitURL

    // let anchor = document.createElement("a")
    // anchor.href = window.URL.createObjectURL(blob)

    // anchor.textContent = "dummy"
    // anchor.style.display = "none"
    // anchor.target = "_blank"
    // document.body.appendChild(anchor)
    // gotoFileFlag = true
    // anchor.click()
    // gotoFileFlag = false

    // if (anchor.download) {
    //   SnackAdd(T("SYSFileDownloadFinished"), "Success")
    // }
    // setTimeout(() => {
    //   document.body.removeChild(anchor)
    // }, 3000)
  }
}

let TCache = new Map()
export const ResetT = () => {
  TCache = new Map()
}
let autoAdded = new Set()

export const T = (
  key: string | undefined,
  reps?: any[] | any,
  returnIfNotFound?: "Key" | "Empty"
): string | undefined => {
  if (reps === undefined) {
    reps = []
  } else if (!Array.isArray(reps)) {
    reps = [reps]
  }
  reps = reps.map((m) => (m === undefined ? "" : m))
  if (SYSConfig.translation?.showOnlyKeys) {
    return `${key} ${reps.join(",")}`
  }
  try {
    if (key === undefined) {
      return undefined
    }
    reps.forEach((rep, i) => {
      if (rep !== undefined) {
        if (typeof rep !== "string") {
          if (rep === null) {
            reps[i] = ""
          }
          // else {
          //   reps[i] = rep.toString()
          // }
        }
      }
    })
    let state = ovl.getState()
    let translations = state.ovl.language.translations
    let translationsVariationId = state.ovl.app.translationsVariationId
    if (translationsVariationId) {
      let commonTranslationsDBKey = `${key.substring(
        0,
        3
      )}${translationsVariationId}${key.substring(3)}`
      if (translations[commonTranslationsDBKey] !== undefined) {
        key = commonTranslationsDBKey
      }
    }
    let cacheKey = key
    if (reps) {
      cacheKey += reps.join()
    }
    let cacheRes = TCache.get(cacheKey)
    if (cacheRes !== undefined) {
      // make sure it gets always tracked...
      translations[key]
      return cacheRes
    }

    // 1st pass
    let res = _T(key, reps, returnIfNotFound)
    // 2nd pass for links and eventually DataPoints {V.} later
    let tres = _SecondPassPlaceholders(res)

    if (tres.length > 0) {
      TCache.set(cacheKey, tres)
      return tres
    }

    return res
  } catch (e) {
    return e.toString().replace('"Error:', "")
  }
}

const _SecondPassPlaceholders = (res: string) => {
  let templates: string[] = []
  if (res.indexOf("{L.") > -1 || res.indexOf("{V.") > -1) {
    // we need to return html templates for this because it contains V. or L. - Codes
    // just work through the res string from left to right and build up an array of Templates
    let startCharPos = 0

    while (true) {
      let endCharPos = res.indexOf("{", startCharPos)
      if (endCharPos < 0) {
        break
      }
      templates.push(`${res.substring(startCharPos, endCharPos)}`)
      startCharPos = endCharPos + 1
      endCharPos = res.indexOf("}", startCharPos)
      let dynamicPart = res.substring(startCharPos, endCharPos)

      if (dynamicPart.startsWith("L.")) {
        let key = dynamicPart.substring(2)
        let link = key.split("@@Ovl")
        let link1 = link[0]
        let linkDesc = link1
        if (link.length > 1) {
          linkDesc = link[1]
        }
        let linkTemplate = `<a href="${link[0]}" target="_blank">${linkDesc}</a>`

        templates.push(`${linkTemplate}`)
      } else if (dynamicPart.startsWith("V.")) {
        let key = dynamicPart.substring(2)
        templates.push(`${resolvePath(ovl.getState(), key)}`)
      }
      startCharPos = endCharPos + 1
    }
  }
  return templates.join("")
}

const _T = (
  key: string,
  reps?: string[],
  returnIfNotFound?: "Key" | "Empty"
): any => {
  if (!languageRef) {
    languageRef = ovl.getState().ovl.language
  }
  // for tracking
  // because when used cached T we still need tracking
  languageRef.translations
  languageRef.language

  // check for mobile key and use translation for mobile users to get shorter translations if applicable (key_M)
  if (isMobile()) {
    let mobileKey = key + "_M"
    if (languageRef.translations[mobileKey] !== undefined) {
      key = mobileKey
    }
  }
  let cacheKey = key
  if (reps) {
    cacheKey += reps.join()
  }
  let cacheRes = TCache.get(cacheKey)
  if (cacheRes !== undefined) {
    return cacheRes
  }
  let str = languageRef.translations[key]
  if (str === undefined || str === null) {
    if (returnIfNotFound === "Empty") {
      return ""
    } else if (returnIfNotFound === "Key") {
      return key
    }

    console.log(`SYS Warning: Translation Key ${key} not found!`)
    if (
      SYSConfig._system.isDev &&
      SYSConfig._system.autoAddMissingTranslationsInDevMode &&
      !ovl.getState().ovl.app.offline.api &&
      ovl.getState().ovl.app.ready &&
      PermissionHas("SYSTR", "A")
    ) {
      // some logic to check with part of the key should go in group and which to code
      // (the texts need to be adjusted then anyway)
      let possibleGroupKeys = Object.keys(ovl.getActions().app.features)
      let foundKeys = possibleGroupKeys.filter((f) => key.indexOf(f) > -1)
      let group = ""
      let code = ""
      if (foundKeys.length > 0) {
        let startIndex = key.indexOf(foundKeys[0])
        let endIndex = startIndex + foundKeys[0].length
        group = key.substring(0, endIndex)
        code = key.replace(group, "")
        if (!code) {
          group = key.substring(0, endIndex / 2)
          code = key.replace(group, "")
        }
      } else {
        let res = _splitCamelCaseWithAbbreviations(key)
        if (res.length > 5) {
          let to = Math.floor(key.length / 2)
          group = key.substring(0, to)
          code = key.substring(to, key.length)
        } else {
          if (res.length > 3) {
            group = res[0]
            res.splice(0, 1)
            group = group + res[0]
            res.splice(0, 1)
          } else {
            group = res[0]
            res.splice(0, 1)
          }

          code = res.join("")
        }
      }
      if (code && group && !autoAdded.has(`${group}${code}`)) {
        autoAdded.add(`${group}${code}`)
        let row = { U_TransGroup: group, U_TransCode: code }
        ovl.getState().ovl.language.availableTranslations.forEach((l) => {
          row[`U_${l}`] = `SYSAutoAdded_${code}`
        })
        setTimeout(async () => {
          //@ts-ignore
          try {
            await TableSaveRow("SYSTranslation", row)
          } catch (error) {
            let err: _TableRowError = error
            // in this context its always add
            // so just remove the row in client
            err.handled(true)
          }
        }, 0)
      }
    }
    return key
  }
  if (str.split("{").length !== str.split("}").length) {
    return str + " invalid{}"
  }
  let hashRes = new Set()
  try {
    str
      .split("{")
      .filter((f) => f.indexOf("}") > -1)
      .map((m) => m.substring(0, m.indexOf("}")))
      .forEach((s) => {
        hashRes.add(s)
      })
    hashRes.forEach((s: string) => {
      let key: string = ""
      if (s.startsWith("T.")) {
        // its a translation itself eg. {T.AppMandatoryHint}
        key = s.substring(2)
        //console.log("found translation key: " + key)
        str = str.split("{" + s + "}").join(_T(key, reps))
      } /* skip V and L, they are handled in second pass */ else if (
        s.startsWith("L.") ||
        s.startsWith("V.")
      ) {
      } else {
        // needs to be a {0}, or {1}, ....
        // so use the replaces array parameter
        let idx = parseInt(s)
        if (!isNaN(idx)) {
          let rep = ""
          if (reps && idx < reps.length) {
            rep = reps[idx]
          }
          str = str.split("{" + s + "}").join(rep)
        }
      }
    })
    TCache.set(cacheKey, str)
    return str
    //}
  } catch (e) {
    console.error(e)
    throw Error(`"${e} in translation string: ${str}`)
  }
}

function _splitCamelCaseWithAbbreviations(s) {
  return s.split(/([A-Z][a-z]+)/).filter(function (e) {
    return e
  })
}

export const enableBodyScroll = (force?: boolean) => {
  let el = document.getElementsByTagName("body")[0]
  if (el.scrollHeight > window.innerHeight || force) {
    if (isMobile()) {
      el.classList.remove("ovl-body-noscroll-mobile")
    } else {
      el.classList.remove("ovl-body-noscroll")
    }

    el.classList.add("ovl-body-scroll")
  }
}

export const disableBodyScroll = (force?: boolean) => {
  let el = document.getElementsByTagName("body")[0]

  if (el.scrollHeight > window.innerHeight || force) {
    el.classList.remove("ovl-body-scroll")

    if (isMobile()) {
      el.classList.add("ovl-body-noscroll-mobile")
    } else {
      el.classList.add("ovl-body-noscroll")
    }
  }
}

export const resolvePath = (object, path, defaultValue?) => {
  return path.split(".").reduce((o, p) => (o ? o[p] : defaultValue), object)
}

export type Translation = {
  [key: string]: string
}

export const _SetFeatureInfo = (
  userId: number,
  feature: SYSFeatures,
  Text1: string,
  Text2: string,
  Text3: string,
  Json1: string,
  Val1: number,
  Val2: number
) => {
  let usersstate = ovl.getState().ovl.users
  if (!usersstate[userId]) {
    //@ts-ignore
    usersstate[userId] = { features: {} }
  }
  let userfeaturestate = usersstate[userId].features
  userfeaturestate[feature] = {
    Code: feature,
    Text1,
    Text2,
    Text3,
    Json1,
    Val1,
    Val2,
    RoleDesc: "",
  }
}

export const _DeleteFeatureInfo = (userId: number, feature: SYSFeatures) => {
  let usersstate = ovl.getState().ovl.users
  if (usersstate[userId]?.features?.[feature] !== undefined) {
    delete usersstate[userId]?.features?.[feature]
  }
}
